import { ALGOLIA_CREDENTIALS_FETCHERS_TOKEN } from './algolia-injection.token';
import { Injectable, inject } from '@angular/core';
import { algoliasearch } from 'algoliasearch';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap, shareReplay } from 'rxjs/operators';

export interface ApiKey {
  id: string;
  key: string;
}

export interface Credentials {
  apiKeys: ApiKey[];
  appId: string;
  expires: number; // timestamp in seconds
}

export interface CredentialsFetcher {
  fetchCredentials(): Observable<Credentials>;
}

export interface CredentialsFetchers {
  [keyId: string]: CredentialsFetcher;
}

@Injectable({ providedIn: 'root' })
export class AlgoliaClientProvider {
  readonly #credentialsFetchers = inject(ALGOLIA_CREDENTIALS_FETCHERS_TOKEN);

  readonly #clientFactories = new Map<CredentialsFetcher, Observable<ClientFactory>>();

  /**
   * The `keyId` should exists in the credentialsFetchers passed to the constructor.
   */
  getClient(keyId: string): Observable<ReturnType<typeof algoliasearch>> | undefined {
    const client = this.#getClientFactory(keyId);

    if (!client) {
      return undefined;
    }

    return client
      .pipe(
        mergeMap((f) => {
          if (f.expired) {
            this.#clientFactories.delete(this.#credentialsFetchers[keyId]);
            const client = this.#getClientFactory(keyId);
            if (client) {
              return client;
            }
          }

          return of(f);
        })
      )
      .pipe(map((f) => f.get(keyId)));
  }

  #getClientFactory(keyId: string): Observable<ClientFactory> | undefined {
    if (!this.#credentialsFetchers[keyId]) {
      return throwError(() => new Error(`No credentials fetcher defined for key: ${keyId}`));
    }

    const fetcher = this.#credentialsFetchers[keyId];

    if (!this.#clientFactories.has(fetcher)) {
      this.#clientFactories.set(
        fetcher,
        fetcher
          .fetchCredentials()
          .pipe(map((c) => new ClientFactory(c)))
          .pipe(shareReplay())
      );
    }

    return this.#clientFactories.get(fetcher);
  }
}

/**
 * Only create 1 instance of algolia.Client per index
 */
class ClientFactory {
  readonly #clientsCache: { [keyId: string]: ReturnType<typeof algoliasearch> } = {};

  constructor(readonly credentials: Credentials) {}

  /**
   * Creates algolia clients.
   *
   * The passed key ID should exists in the credentials
   * given in the constructor.
   */
  get(keyId: string): ReturnType<typeof algoliasearch> {
    if (!this.#clientsCache[keyId]) {
      const apiKey = this.credentials.apiKeys.find((e) => e.id === keyId);

      if (apiKey === undefined) {
        throw new Error(`Cannot find key with id: ${keyId}.`);
      }

      this.#clientsCache[keyId] = algoliasearch(this.credentials.appId, apiKey.key);
    }

    return this.#clientsCache[keyId];
  }

  get expired(): boolean {
    return Math.floor(Date.now() / 1000) > this.credentials.expires; // 30secs before actual expiration
  }
}
